/**
 * Drawing feature utility functions
 *
 * Extracted from drawing.store.ts for better modularity.
 * Contains pure functions for calculations and canvas operations.
 */

import type { ConstrainedDimensions } from './drawing.types';

// Maximum canvas size in pixels (1 megapixel = 1,000,000 pixels)
export const MAX_CANVAS_PIXELS = 1_000_000;

/**
 * Convert slider value (0-100) to brush size (2-100px)
 * Uses exponential curve for finer control at small sizes
 */
export const sliderToBrushSize = (sliderValue: number): number => {
  const normalized = sliderValue / 100;
  const exponential = Math.pow(normalized, 2.2);
  return Math.max(2, Math.round(2 + exponential * 98));
};

/**
 * Convert brush size (2-100px) to slider value (0-100)
 * Inverse of sliderToBrushSize
 */
export const brushSizeToSlider = (brushSize: number): number => {
  const normalized = (brushSize - 2) / 98;
  const sliderValue = Math.pow(normalized, 1 / 2.2) * 100;
  return Math.round(sliderValue);
};

/**
 * Calculate constrained dimensions while preserving aspect ratio
 * Used to limit canvas size to MAX_CANVAS_PIXELS
 */
export const calculateConstrainedDimensions = (
  width: number,
  height: number,
  maxPixels: number = MAX_CANVAS_PIXELS
): ConstrainedDimensions => {
  const totalPixels = width * height;

  if (totalPixels <= maxPixels) {
    return { width, height, isConstrained: false };
  }

  // Calculate scale factor to fit within maxPixels
  const scale = Math.sqrt(maxPixels / totalPixels);

  // Use floor to guarantee result is within limit
  return {
    width: Math.floor(width * scale),
    height: Math.floor(height * scale),
    isConstrained: true,
  };
};

/**
 * Wrap text to fit within a given width
 * Used for rendering text blocks to canvas
 */
export const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] => {
  if (!text) return [''];

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word, index) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && currentLine) {
      // Line is too long, push current line and start new one
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }

    // Last word - push the line
    if (index === words.length - 1 && currentLine) {
      lines.push(currentLine);
    }
  });

  // Return at least one line even if empty
  return lines.length > 0 ? lines : [''];
};

/**
 * Compare two ImageData objects for equality
 * Uses sampling for performance (checks every 400th pixel)
 */
export const areImagesEqual = (
  imageData1: ImageData,
  imageData2: ImageData
): boolean => {
  if (
    imageData1.width !== imageData2.width ||
    imageData1.height !== imageData2.height
  ) {
    return false;
  }

  const data1 = imageData1.data;
  const data2 = imageData2.data;

  // Sample check for performance
  for (let i = 0; i < data1.length; i += 400) {
    if (
      data1[i] !== data2[i] ||
      data1[i + 1] !== data2[i + 1] ||
      data1[i + 2] !== data2[i + 2] ||
      data1[i + 3] !== data2[i + 3]
    ) {
      return false;
    }
  }

  return true;
};
